import {
  DownloadOutlined,
  DownOutlined,
  InfoCircleTwoTone,
  SyncOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Stack, Typography } from '@sureco/design-system'
import { Button, Tooltip } from '@surecompanies/core_components'
import React from 'react'
import { toast } from 'react-toastify'
import ReactTooltip from 'react-tooltip'
import styles from './ApplicationsList.module.scss'

// hixme-ui
import Container from '@hixme-ui/container'
import { FormatDate } from '@hixme-ui/dates'
import Icon from '@hixme-ui/icon'
import Text from '@hixme-ui/text'

// project
import { Checkbox, DatePicker, Dropdown, MenuProps } from 'antd'
import RenewalIcon from 'assets/icons/repeat.svg'
import Menu, { MenuItem } from 'components/Menu'
import SearchTable from 'components/SearchTable'
import SortableTh from 'components/SortableTh'
import SplitButton from 'components/SplitButton'
import dayjs from 'dayjs'
import { ASSIGNEE_LIST_V2 } from 'enrollment-platform/pages/ApplicationQueue/constants'
import { getNameToShow } from 'routes/ApplicationManager/utils/applicationList'
import { ApplicationInfo } from 'types/application'
import useExportApplicationsCsv from '../../hooks/useExportApplicationsCsv'
import RenewalApplicationsButton from '../RenewalApplicationsButton/RenewalApplicationsButton'

export interface updateAssigneeProps {
  benefits: string[]
  assignee: string
}

export type ApplicationsListProps = {
  applications?: ApplicationInfo[]
  selectedApplications?: string[]
  changeSort: (key: string) => void
  getAllApplications: (params?: {
    SkipCache?: boolean
    BenefitEffectiveDate?: string
    BenefitEffectiveDateRange?: string[]
  }) => void
  hasMore: boolean
  openDetails: (applicationId: string) => void
  openNotes: (employeePublicKey: string) => void
  openMessages: (employeePublicKey: string) => void
  sortBy: string
  sortDescending: boolean
  createRefreshNotification: (message: string) => void
  viewMore: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  viewAll: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  setBenefitEffectiveDateChangeForQuery: (dateString: string) => void
  setBenefitEffectiveDateRangeChangeForQuery: (datesString: string[]) => void
  benefitEffectiveDateForQuery: string
  benefitEffectiveDateRangeForQuery: string[]
  filterByBenefitEffectiveDateRange: boolean
  defaultSkipCache: boolean
  setSelectedApplications: (selectedApplications: string[]) => void
  updateAssignee: (body: updateAssigneeProps) => void
  isUpdatingAssignee: boolean
  exportToCsv: () => void
  allApplications: ApplicationInfo[]
}

const ApplicationsList = ({
  applications = [],
  selectedApplications = [],
  changeSort,
  getAllApplications,
  hasMore,
  openDetails,
  openNotes,
  openMessages,
  sortBy,
  sortDescending,
  createRefreshNotification,
  viewMore,
  viewAll,
  setBenefitEffectiveDateChangeForQuery,
  setBenefitEffectiveDateRangeChangeForQuery,
  benefitEffectiveDateForQuery,
  benefitEffectiveDateRangeForQuery,
  filterByBenefitEffectiveDateRange,
  defaultSkipCache,
  setSelectedApplications,
  updateAssignee,
  isUpdatingAssignee,
  allApplications,
}: ApplicationsListProps) => {
  console.log(
    'filterByBenefitEffectiveDateRange',
    filterByBenefitEffectiveDateRange
  )
  console.log(
    'benefitEffectiveDateRangeForQuery',
    benefitEffectiveDateRangeForQuery
  )
  console.log('benefitEffectiveDateForQuery', benefitEffectiveDateForQuery)
  const { exportToCsv } = useExportApplicationsCsv(
    benefitEffectiveDateForQuery,
    allApplications
  )
  const items: MenuProps['items'] = ASSIGNEE_LIST_V2.map((value) => {
    return {
      label: value,
      key: value,
      icon: <UserOutlined />,
    }
  })

  const menuProps = {
    items,
    onClick: (e) => {
      if (e?.key) {
        handleAssignToClick(e.key)
      } else {
        toast.error('Please select an assignee again')
      }
    },
  }

  const handleAssignToClick = (assignee) => {
    // call api EPS benefit assign endpoint with key and list of selectedApplications
    updateAssignee({ assignee, benefits: selectedApplications })
  }

  const handleBenefitEffectiveDateChange = (_, dateString) => {
    setBenefitEffectiveDateChangeForQuery(dateString)
    getAllApplications({
      BenefitEffectiveDate: dateString,
      SkipCache: defaultSkipCache,
    })
    createRefreshNotification('Loading...')
  }

  const handleBenefitEffectiveDateRangeChange = (dates) => {
    setBenefitEffectiveDateRangeChangeForQuery(dates)
    getAllApplications({
      BenefitEffectiveDateRange: dates,
      SkipCache: defaultSkipCache,
    })
    createRefreshNotification('Loading...')
  }

  const handleRefreshClick = () => {
    getAllApplications({
      SkipCache: true,
      ...(benefitEffectiveDateForQuery && {
        BenefitEffectiveDate: benefitEffectiveDateForQuery,
      }),
      ...(benefitEffectiveDateRangeForQuery && {
        BenefitEffectiveDateRange: benefitEffectiveDateRangeForQuery,
      }),
    })
    createRefreshNotification('Refreshing...')
  }

  const handleApplicationSelect = (applicationId) => {
    if (selectedApplications.includes(applicationId)) {
      setSelectedApplications(
        selectedApplications.filter((id) => id !== applicationId)
      )
      return
    }
    setSelectedApplications([...selectedApplications, applicationId])
  }

  const handleSelectAll = (e) => {
    setSelectedApplications(
      e.target.checked ? applications.map((app) => app.Id) : []
    )
  }

  const checkAll = selectedApplications.length === applications?.length
  const indeterminate =
    selectedApplications.length > 0 &&
    selectedApplications.length < applications?.length

  return (
    <div>
      {benefitEffectiveDateRangeForQuery && (
        <Stack
          direction="row"
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 0',
          }}
        >
          <Stack direction="row" space={5} style={{ alignItems: 'center' }}>
            <Typography size={16} color="secondary">
              Show all plans with a Benefit Effective Date in the range
            </Typography>
            <DatePicker.RangePicker
              allowClear={false}
              format="MM-DD-YYYY"
              // Set defaultValue to the current date and the date three months ahead
              defaultValue={[
                dayjs(benefitEffectiveDateRangeForQuery[0]),
                dayjs(benefitEffectiveDateRangeForQuery[1]),
              ]}
              onChange={handleBenefitEffectiveDateRangeChange}
            />
            <Typography size={16} color="secondary">
              .
            </Typography>
          </Stack>
        </Stack>
      )}
      {benefitEffectiveDateForQuery && (
        <Stack
          direction="row"
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 0',
          }}
        >
          <Stack direction="row" space={5} style={{ alignItems: 'center' }}>
            <Typography size={16} color="secondary">
              Show all plans with a Benefit Effective Date of
            </Typography>
            <DatePicker
              allowClear={false}
              format="MM-DD-YYYY"
              defaultValue={dayjs(benefitEffectiveDateForQuery, 'MM-DD-YYYY')}
              onChange={handleBenefitEffectiveDateChange}
            />
            <Typography size={16} color="secondary">
              or later.
            </Typography>
            <Typography size={16} color="secondary">
              .
            </Typography>
          </Stack>
        </Stack>
      )}
      <Stack
        direction="row"
        style={{
          padding: '10px 0',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack direction="row" space={2}>
          <Text span strong style={{ padding: '0 2em 0 0' }}>
            Legend
          </Text>
          <span style={{ position: 'relative', top: '2px' }}>
            <Icon automated light />
          </span>
          <Text span light thin style={{ padding: '0 2em 0 0' }}>
            = automated{' '}
          </Text>
          <span style={{ position: 'relative', top: '2px' }}>
            <Icon split light />
          </span>
          <Text span light thin style={{ padding: '0 2em 0 0' }}>
            = split{' '}
          </Text>
          <span style={{ position: 'relative', top: '2px' }}>
            <Icon alert light />
          </span>
          <Text span light thin style={{ padding: '0 2em 0 0' }}>
            = needs attention{' '}
          </Text>
          <span style={{ position: 'relative', top: '2px' }}>
            <Icon shieldCross light size={1.25} />
          </span>
          <Text span light thin style={{ padding: '0 2em 0 0' }}>
            = same carrier{' '}
          </Text>
        </Stack>
        <Stack direction="row" space={2}>
          <RenewalApplicationsButton />
          <Button
            color="default"
            variant="filled"
            icon={<SyncOutlined />}
            onClick={handleRefreshClick}
          >
            <Typography>Refresh</Typography>
          </Button>
          <Button
            color="default"
            variant="filled"
            icon={<DownloadOutlined />}
            onClick={exportToCsv}
            disabled={!applications.length}
          >
            <Typography>Export CSV</Typography>
          </Button>
          <Dropdown
            menu={menuProps}
            trigger={['click']}
            disabled={!selectedApplications.length || isUpdatingAssignee}
            dropdownRender={(menu) => (
              <div className={styles.dropdownContainer}>{menu}</div>
            )}
          >
            <Button color={'default'} variant={'filled'}>
              <Typography>
                Assign to <DownOutlined />
              </Typography>
            </Button>
          </Dropdown>
        </Stack>
      </Stack>
      {applications && (
        <div className={styles.stickyTableContainer}>
          <SearchTable>
            <thead>
              <tr>
                <th>
                  <Checkbox
                    indeterminate={indeterminate}
                    checked={checkAll}
                    onChange={handleSelectAll}
                  />
                </th>
                <SortableTh
                  showIcon={sortBy === 'name'}
                  onClick={() => changeSort('name')}
                  sortDescending={sortDescending}
                >
                  Name
                </SortableTh>
                <SortableTh
                  showIcon={sortBy === 'cartCommitted'}
                  onClick={() => changeSort('cartCommitted')}
                  sortDescending={sortDescending}
                >
                  Cart status
                </SortableTh>
                <SortableTh>Benefit ID</SortableTh>
                <SortableTh
                  showIcon={sortBy === 'renewal'}
                  onClick={() => changeSort('renewal')}
                  sortDescending={sortDescending}
                >
                  Application Type
                </SortableTh>
                <SortableTh
                  showIcon={sortBy === 'benefitEffectiveDate'}
                  onClick={() => changeSort('benefitEffectiveDate')}
                  sortDescending={sortDescending}
                >
                  Ben Eff Date
                </SortableTh>
                <SortableTh
                  showIcon={sortBy === 'carrier'}
                  onClick={() => changeSort('carrier')}
                  sortDescending={sortDescending}
                >
                  Carrier
                </SortableTh>
                <SortableTh
                  showIcon={sortBy === 'carrierPlanChange'}
                  onClick={() => changeSort('carrierPlanChange')}
                  sortDescending={sortDescending}
                >
                  Carrier Plan Change
                </SortableTh>
                <SortableTh
                  showIcon={sortBy === 'cartCommitted'}
                  onClick={() => changeSort('cartCommitted')}
                  sortDescending={sortDescending}
                >
                  Cart Committed
                </SortableTh>
                <SortableTh
                  showIcon={sortBy === 'commissionPartner'}
                  onClick={() => changeSort('commissionPartner')}
                  sortDescending={sortDescending}
                >
                  Commission
                </SortableTh>
                <SortableTh
                  showIcon={sortBy === 'hixmeSubmitted'}
                  onClick={() => changeSort('hixmeSubmitted')}
                  sortDescending={sortDescending}
                >
                  SureCo Submitted
                </SortableTh>
                <SortableTh
                  showIcon={sortBy === 'memberId'}
                  onClick={() => changeSort('memberId')}
                  sortDescending={sortDescending}
                >
                  Member Id
                </SortableTh>
                <SortableTh>State</SortableTh>
                <SortableTh
                  showIcon={sortBy === 'appState'}
                  onClick={() => changeSort('appState')}
                  sortDescending={sortDescending}
                >
                  Status
                </SortableTh>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => {
                const carrierApp =
                  'CarrierApplication' in app
                    ? app.CarrierApplication
                    : undefined
                return (
                  <tr key={app.Id}>
                    <td>
                      <div
                        style={{
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Checkbox
                          style={{
                            paddingRight: '4px',
                          }}
                          checked={selectedApplications?.includes(app.Id)}
                          onChange={() => {
                            handleApplicationSelect(app.Id)
                          }}
                        />
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                          }}
                        >
                          <div className={styles.iconWrap}>
                            {carrierApp?.IsAutomated && (
                              <Icon automated light size={1.1} />
                            )}
                            {carrierApp?.IsSameCarrier && (
                              <span className={styles.shieldWrap}>
                                <Icon shieldCross light size={1.3} />
                              </span>
                            )}
                            {app.IsBundleSplit && (
                              <Icon split light size={1.1} />
                            )}
                            {carrierApp?.NeedsAttention &&
                              // the api will return an object sometimes. We do not want those to display
                              (carrierApp?.Error &&
                              typeof carrierApp?.Error === 'string' ? (
                                <span>
                                  <span
                                    data-tip=""
                                    data-for={`error-${app.Id}`}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <Icon alert red size={1.1} />
                                  </span>
                                  <ReactTooltip
                                    id={`error-${app.Id}`}
                                    place="bottom"
                                  >
                                    <Text div style={{ maxWidth: '200px' }}>
                                      {carrierApp.Error}
                                    </Text>
                                  </ReactTooltip>
                                </span>
                              ) : (
                                <Icon alert red size={1.1} />
                              ))}
                            {app.NeedsAttentionReason &&
                              Object.values(app.NeedsAttentionReason ?? {})
                                ?.length > 0 && (
                                <Tooltip
                                  title={
                                    <Typography>
                                      Before working this app, please note:{' '}
                                      {Object.entries(
                                        app.NeedsAttentionReason
                                      ).reduce(
                                        (_, [key, value]) =>
                                          `${key} = ${value}`,
                                        ''
                                      )}
                                    </Typography>
                                  }
                                >
                                  <InfoCircleTwoTone twoToneColor="#ff4d2e" />
                                </Tooltip>
                              )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ textAlign: 'left' }}>
                      <Text
                        primary={!app.IsTestUser}
                        red={app.IsTestUser}
                        style={{ lineHeight: '1.2' }}
                        onClick={() => openDetails(app.Id)}
                      >
                        {getNameToShow(app)}
                      </Text>{' '}
                      <Text
                        light
                        primary={!app.IsTestUser}
                        red={app.IsTestUser}
                        thin
                        style={{ lineHeight: '1.2' }}
                      >
                        {app.IsTestUser && '(Test User)'}
                      </Text>
                      <Text
                        fontSize={'10px'}
                        light
                        style={{
                          display: 'block',
                          lineHeight: '1.2',
                        }}
                      >
                        {app.CompanyName}
                      </Text>
                      <Text
                        fontSize={'10px'}
                        light
                        style={{
                          display: 'block',
                          lineHeight: '1.2',
                          paddingTop: '4px',
                          paddingBottom: '4px',
                          color: '#00A0A8',
                        }}
                      >
                        {app.Assignee && `Assigned to: ${app.Assignee}`}
                      </Text>
                    </td>
                    <td>
                      <Text thin>{app.CartCommitted ? 'Closed' : 'Open'}</Text>
                    </td>
                    <td>
                      <Text thin small>
                        {app.Id}
                      </Text>
                    </td>
                    <td>
                      {app.RenewalInformation &&
                      app.RenewalInformation.isRenewal ? (
                        <RenewalIcon width="16" height="16" />
                      ) : (
                        '--'
                      )}
                    </td>
                    <td>
                      <Text thin>
                        <FormatDate thin date={app.BenefitEffectiveDate} />
                      </Text>
                    </td>
                    <td>
                      <Text thin>{app.CarrierName}</Text>
                    </td>
                    <td>
                      {app?.CarrierPlanChange
                        ? `${app.CarrierPlanChange}`.toLowerCase()
                        : '--'}
                    </td>
                    <td>
                      <FormatDate thin date={carrierApp?.WorkerSubmittedDate} />
                    </td>
                    <td>
                      <Text thin>{app.CommissionPartner ?? '--'}</Text>
                    </td>
                    <td>
                      {carrierApp?.HixmeSubmittedDate ? (
                        <FormatDate thin date={carrierApp.HixmeSubmittedDate} />
                      ) : (
                        '--/--/--'
                      )}
                    </td>
                    <td>
                      <Text thin>{app.PolicyNumber ?? '--'}</Text>
                    </td>
                    <td>
                      <Text thin>{app.StateProvince ?? '--'}</Text>
                    </td>
                    <td>
                      <Text thin>
                        {carrierApp?.Status === 'Validated'
                          ? 'Verified'
                          : carrierApp?.Status}
                      </Text>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <SplitButton
                        title="Open"
                        onClick={() => openDetails(app.Id)}
                      >
                        <Menu>
                          <MenuItem onClick={() => openDetails(app.Id)}>
                            <Container flex noPadding>
                              <Icon newWindow light />
                              <Text
                                light
                                thin
                                style={{
                                  display: 'inline-block',
                                  paddingLeft: '10px',
                                  lineHeight: '1.2',
                                }}
                              >
                                Application Detail
                              </Text>
                            </Container>
                          </MenuItem>
                          <MenuItem
                            onClick={() => openNotes(app.EmployeePublicKey)}
                          >
                            <Container flex noPadding>
                              <Icon noteAdd light />
                              <Text
                                light
                                thin
                                style={{
                                  display: 'inline-block',
                                  paddingLeft: '10px',
                                  lineHeight: '1.2',
                                }}
                              >
                                Add Notes
                              </Text>
                            </Container>
                          </MenuItem>
                          <MenuItem
                            onClick={() => openMessages(app.EmployeePublicKey)}
                          >
                            <Container flex noPadding>
                              <Icon message light />
                              <Text
                                light
                                thin
                                style={{
                                  display: 'inline-block',
                                  paddingLeft: '10px',
                                  lineHeight: '1.2',
                                }}
                              >
                                View Messages
                              </Text>
                            </Container>
                          </MenuItem>
                          <MenuItem divider />
                          <MenuItem
                            url={`/employees/${app.EmployeePublicKey}/coverage`}
                          >
                            <Container flex noPadding>
                              <Icon message light />
                              <Text
                                light
                                thin
                                style={{
                                  display: 'inline-block',
                                  paddingLeft: '10px',
                                  lineHeight: '1.2',
                                }}
                              >
                                Coverage Summary
                              </Text>
                            </Container>
                          </MenuItem>
                        </Menu>
                      </SplitButton>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </SearchTable>
        </div>
      )}
      {hasMore && (
        <Container flex width="100%" style={{ gap: 10, padding: '20px 0' }}>
          <Button block size="large" onClick={viewMore}>
            View more
          </Button>
          <Button block size="large" onClick={viewAll}>
            View all
          </Button>
        </Container>
      )}
    </div>
  )
}

export default ApplicationsList
